import React, { type ReactNode } from 'react';

import { startCase } from 'lodash';
import styled from 'styled-components';

import {
  Badge,
  Chip,
  Flex,
  Heading,
  type IBadgeVariant,
  type IconType,
  Text,
  colors,
  spacing,
  styleIf,
  useStylesFor as stylesFor,
} from '@sine/sine-ui-components';

import MiddleDotSeparator from './MiddleDotSeparator';
import Container from './container';

export enum VersionEnum {
  alpha = 'alpha',
  beta = 'beta',
}

interface ButtonsAlignmentProps {
  leftButtons?: boolean;
}

interface PageToolbarProps {
  title: string;
  subtitle?: string;
  buttons?: ReactNode | Array<ReactNode>;
  version?: `${VersionEnum}`;
  leftButtons?: boolean;
  subtitleBadge?: { variant: IBadgeVariant; icon?: IconType; text: string };
  className?: string;
  fullWidth?: boolean;
}

const OuterContainer = styled.div`
  background: ${colors.white};
  margin-bottom: ${spacing(2)};
  padding: ${spacing(4)} 0;
  min-height: 60px;
  max-height: 128px;

  ${Container} {
    height: 100%;
  }
`;

const CollapsibleFlex = styled(Flex)`
  ${stylesFor('tablet').andSmaller`
    display: none;
  `}
`;

const StyledHeading = styled(Heading)<{ marginRight: number }>`
  flex-grow: 0;
  margin-right: ${({ marginRight }) => spacing(marginRight)};
`;

const ButtonsAlignment = styled.div<ButtonsAlignmentProps>`
  flex-grow: 1;
  display: flex;

  ${({ leftButtons }) => styleIf(!leftButtons)`
    justify-content: flex-end;
  `}
`;

// StyledChip needs to be revisited on phase-5
const StyledChip = styled(Chip)`
  background: ${colors.primary500};
  border-radius: 3.7rem;
  color: ${colors.white};
  margin-right: ${spacing(2)};
`;

const PageTitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

function PageTitle({ version, title }: { version?: `${VersionEnum}`; title: string }) {
  return (
    <PageTitleContainer>
      <StyledHeading type='h2' marginRight={version ? 1 : 2}>
        {title}
      </StyledHeading>
      {version && <StyledChip variant='tag'>{startCase(VersionEnum[version])}</StyledChip>}
    </PageTitleContainer>
  );
}

const ColumnBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing(2)} 0;

  ${StyledHeading} {
    margin: 0px;
  }
`;

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;

  ${stylesFor('mobile').only`
    flex-direction: column;
    align-items: flex-start;
  `}
`;

export default function PageToolbar({
  title,
  subtitle,
  buttons,
  version,
  leftButtons,
  subtitleBadge,
  className,
  fullWidth,
}: PageToolbarProps) {
  return (
    <OuterContainer data-st-ext='page-toolbar' className={className}>
      <Container flex fullWidth={fullWidth}>
        {subtitle ? (
          <ColumnBlock>
            <PageTitle title={title} version={version} />
            <Flex alignItems='center'>
              {subtitleBadge && (
                <CollapsibleFlex>
                  <Badge variant={subtitleBadge.variant} icon={subtitleBadge.icon}>
                    {subtitleBadge.text}
                  </Badge>
                  <MiddleDotSeparator />
                </CollapsibleFlex>
              )}
              <Text colorVariant='text68'>{subtitle}</Text>
            </Flex>
          </ColumnBlock>
        ) : (
          <HeadingContainer>
            <PageTitle title={title} version={version} />
          </HeadingContainer>
        )}

        {buttons && (
          <ButtonsAlignment leftButtons={leftButtons} data-st-ext='toolbar-end-content button-active-passes'>
            {buttons}
          </ButtonsAlignment>
        )}
      </Container>
    </OuterContainer>
  );
}
